import {gql} from '@apollo/client';
import React, {useEffect} from 'react';

import {setWindowTitle} from 'BenchlingUtil';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import getTestProps from 'test/lib/cypress/getTestProps';

import {GetBenchlingAppDefinitionVersions} from './__generated/GetBenchlingAppDefinitionVersions';
import {ListBenchlingAppDefinitionsQuery} from './__generated/ListBenchlingAppDefinitionsQuery';
import {
  ListBenchlingAppInstallationsQuery,
  ListBenchlingAppInstallationsQueryVariables,
} from './__generated/ListBenchlingAppInstallationsQuery';
import BenchlingAppListing from './BenchlingAppListing';

export type AppInstallation =
  ListBenchlingAppInstallationsQuery['benchlingAppListing']['benchlingApps'][number];
export type AppDefinition =
  ListBenchlingAppDefinitionsQuery['benchlingAppDefinitionDeveloperListing']['appDefinitions'][number];
export type AppDefinitionVersion = ExtractByTypename<
  GetBenchlingAppDefinitionVersions['benchlingAppDefinition'],
  'BenchlingAppDefinitionNode'
>['versions'][number];

export const BENCHLING_APP_LISTING_QUERY_PAGE_SIZE = 150;
export const BENCHLING_APP_INSTALLATIONS_LISTING_QUERY = gql`
  query ListBenchlingAppInstallationsQuery($limitOffsetInput: LimitOffsetInput!, $forDeveloper: StrictBoolean)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingAppListing(limitOffsetInput: $limitOffsetInput, forDeveloper: $forDeveloper) {
      benchlingApps {
        ...BenchlingAppGraphQL
      }
    }
  }
  ${getFragmentWrapper('BenchlingApp').fragment}
`;

export const BENCHLING_APP_DEFINITIONS_LISTING_QUERY = gql`
  query ListBenchlingAppDefinitionsQuery @skipAudit @engComponent(name: BENCHLING_APPS) {
    benchlingAppDefinitionDeveloperListing {
      appDefinitions {
        ...BenchlingAppDefinitionGraphQL
      }
    }
  }
  ${getFragmentWrapper('BenchlingAppDefinition').fragment}
`;

// We only ever request the first 150 results. When we GA Benchling apps, we will need to add a proper paging UI.
export const BENCHLING_APP_LISTING_QUERY_VARIABLES: ListBenchlingAppInstallationsQueryVariables = {
  limitOffsetInput: {
    limit: BENCHLING_APP_LISTING_QUERY_PAGE_SIZE,
    offset: 0,
  },
  forDeveloper: true,
};

export default function BenchlingAppsPage(): JSX.Element | null {
  useEffect(() => {
    setWindowTitle('Apps');
  }, []);

  const installationsQueryResult = useBenchlingQuery<
    ListBenchlingAppInstallationsQuery,
    ListBenchlingAppInstallationsQueryVariables
  >(BENCHLING_APP_INSTALLATIONS_LISTING_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: BENCHLING_APP_LISTING_QUERY_VARIABLES,
  });

  const definitionsQueryResult = useBenchlingQuery<ListBenchlingAppDefinitionsQuery>(
    BENCHLING_APP_DEFINITIONS_LISTING_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  let appInstallations: Array<AppInstallation>;
  let appDefinitions: Array<AppDefinition>;
  if (
    installationsQueryResult.type === QueryResultType.ERROR ||
    definitionsQueryResult.type === QueryResultType.ERROR
  ) {
    return null;
  } else if (
    installationsQueryResult.type === QueryResultType.PENDING ||
    definitionsQueryResult.type === QueryResultType.PENDING
  ) {
    return (
      <div
        className="settingsPage font-proxima-13"
        {...getTestProps({component: 'BenchlingAppsPage', element: 'root'})}
      >
        <div className="settingsPage-contentWrapper">
          <BenchlingAppListing appInstallations={null} appDefinitions={null} forceRefetchApps={() => {}} />
        </div>
      </div>
    );
  } else {
    appInstallations = filterCurrentTenantApps(
      installationsQueryResult.data.benchlingAppListing.benchlingApps
    );
    appDefinitions = definitionsQueryResult.data.benchlingAppDefinitionDeveloperListing.appDefinitions;
  }

  return (
    <div
      className="settingsPage font-proxima-13"
      {...getTestProps({component: 'BenchlingAppsPage', element: 'root'})}
    >
      <div className="settingsPage-contentWrapper">
        <BenchlingAppListing
          appInstallations={appInstallations}
          appDefinitions={appDefinitions}
          forceRefetchApps={async () => {
            await installationsQueryResult.refetch();
            await definitionsQueryResult.refetch();
          }}
        />
      </div>
    </div>
  );
}
function filterCurrentTenantApps(benchlingApps: Array<AppInstallation>): Array<AppInstallation> {
  return benchlingApps.filter((app) => app.owningTenant.isCurrentTenant);
}
